// The per-client CFDI panel — TenantDetail → Facturación.
//
// The queue (/admin/cfdi) answers "what is stuck, platform-wide". This answers
// "what exists for THIS client", which is where cancellation decisions get
// made: a substitution shows as two documents under one invoice, with the
// chain visible, and the actions live next to the document they act on.
//
// Cancelling a fiscal document is deliberately heavyweight here: a modal per
// action, the motivo spelled out, and copy that says what the SAT will do —
// because the one thing this screen must never feel like is a delete button.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  cancelCfdi,
  getTenantCfdis,
  substituteCfdiRequest,
  type TenantCfdiInvoice,
} from './cfdi-queue-data.js'
import {
  Button,
  Card,
  Chip,
  EmptyStateCard,
  Modal,
  Spinner,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { QueryError } from '../../lib/QueryError.js'

type ChipTone = 'ok' | 'warn' | 'sens' | 'info' | 'draft'

/** Operator vocabulary — the real states, including the cancellation
 *  lifecycle the clinic side only ever sees the tail of. */
const DOC_STATUS: Record<string, { label: string; tone: ChipTone }> = {
  issued: { label: 'Vigente', tone: 'ok' },
  issued_docs_missing: { label: 'Vigente, sin archivos', tone: 'warn' },
  superseded: { label: 'Sustituido (cancelación pendiente)', tone: 'warn' },
  cancellation_requested: { label: 'Cancelación en el SAT', tone: 'warn' },
  cancelled: { label: 'Cancelado', tone: 'draft' },
  cancellation_rejected: { label: 'Cancelación rechazada — sigue vigente', tone: 'sens' },
}

const MOTIVO_LABEL: Record<'02' | '03', string> = {
  '02': '02 — Comprobante emitido con errores SIN relación (no se sustituye)',
  '03': '03 — No se llevó a cabo la operación',
}

function mxn(centavos: number, currency: string): string {
  return (centavos / 100).toLocaleString('es-MX', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  })
}

function day(iso: string | null): string {
  if (iso === null) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })
}

function DocIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-5"
    >
      <path d="M14 3v5h5" />
      <path d="M19 21H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h9l6 6v11a1 1 0 0 1-1 1Z" />
    </svg>
  )
}

export function CfdiPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const qc = useQueryClient()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'cfdis'],
    queryFn: () => getTenantCfdis(tenantId),
  })
  // The invoice AND the specific document that was clicked: the request pins
  // the uuid, so a racing substitution cannot redirect the action onto a
  // document nobody was looking at.
  const [cancelFor, setCancelFor] = useState<{ inv: TenantCfdiInvoice; uuid: string } | null>(null)
  const [motivo, setMotivo] = useState<'02' | '03'>('02')
  const [substituteFor, setSubstituteFor] = useState<{
    inv: TenantCfdiInvoice
    uuid: string
  } | null>(null)

  const refresh = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId, 'cfdis'] })
  }

  const cancel = useMutation({
    mutationFn: (args: { invoiceId: string; motivo: '02' | '03'; uuid: string }) =>
      cancelCfdi(args.invoiceId, args.motivo, args.uuid),
    onSuccess: async (out) => {
      await refresh()
      setCancelFor(null)
      // "solicitada", never "cancelado", unless the SAT already confirmed —
      // over $1,000 MXN the receiver has three business days, and the poller
      // is the only thing allowed to promote the state.
      toast.ok(
        out.status === 'cancelled'
          ? 'CFDI cancelado — el SAT lo confirmó de inmediato'
          : 'Cancelación solicitada — el SAT tiene la última palabra',
      )
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'No se pudo cancelar el CFDI.'))
    },
  })

  const substitute = useMutation({
    mutationFn: (args: { invoiceId: string; uuid: string }) =>
      substituteCfdiRequest(args.invoiceId, args.uuid),
    onSuccess: async () => {
      await refresh()
      setSubstituteFor(null)
      toast.ok('CFDI sustituido — el nuevo ya está vigente y el anterior en cancelación')
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'No se pudo sustituir el CFDI.'))
    },
  })

  const items = data ?? []
  const withDocs = items.filter((i) => i.documents.length > 0)

  return (
    <Card>
      <Card.Head
        title="CFDI del cliente"
        sub="Cada documento fiscal emitido, con su cadena de sustitución. Cancelar es una decisión de operador, nunca del sistema."
      />
      <Card.Body>
        {isError ? (
          <QueryError message="No pudimos cargar los CFDI." onRetry={() => void refetch()} />
        ) : isLoading ? (
          <Spinner label="Cargando CFDI…" />
        ) : withDocs.length === 0 ? (
          <EmptyStateCard
            icon={<DocIcon />}
            title="Sin CFDI todavía"
            body="Cuando una factura pagada se timbre, sus documentos aparecen aquí con sus acciones."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {withDocs.map((inv) => (
              <li key={inv.invoiceId} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-semibold text-text">
                    {inv.folio ?? 'Sin folio'}
                  </span>
                  <span className="text-[12px] text-text-2">
                    {mxn(inv.amountTotalMxn, inv.currency)} · pagada {day(inv.paidAt)}
                  </span>
                </div>
                <ul className="flex flex-col gap-1.5">
                  {inv.documents.map((doc) => {
                    const s = DOC_STATUS[doc.status] ?? {
                      label: doc.status,
                      tone: 'draft' as ChipTone,
                    }
                    const isLive = doc.status === 'issued' || doc.status === 'issued_docs_missing'
                    return (
                      <li key={doc.uuid} className="flex flex-wrap items-center gap-2">
                        <Chip tone={s.tone} dot>
                          {s.label}
                        </Chip>
                        <span className="font-mono text-[10.5px] text-text-3">{doc.uuid}</span>
                        {doc.replacesUuid !== null && (
                          <span className="text-[10.5px] text-text-3">
                            → sustituido por {doc.replacesUuid.slice(0, 8)}…
                          </span>
                        )}
                        {isLive && (
                          <span className="ml-auto flex gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setSubstituteFor({ inv, uuid: doc.uuid })}
                            >
                              Sustituir
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setCancelFor({ inv, uuid: doc.uuid })}
                            >
                              Cancelar CFDI
                            </Button>
                          </span>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </Card.Body>

      <Modal
        open={cancelFor !== null}
        onClose={() => setCancelFor(null)}
        title="Cancelar CFDI"
        subtitle="La cancelación se solicita al SAT y el receptor puede tener 3 días hábiles para aceptarla. Solo se puede hasta el 31 de enero del año siguiente a la emisión."
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelFor(null)}>
              Conservar el CFDI
            </Button>
            <Button
              disabled={cancel.isPending}
              onClick={() => {
                if (cancelFor !== null)
                  cancel.mutate({
                    invoiceId: cancelFor.inv.invoiceId,
                    motivo,
                    uuid: cancelFor.uuid,
                  })
              }}
            >
              {cancel.isPending ? 'Solicitando…' : 'Solicitar cancelación'}
            </Button>
          </>
        }
      >
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[12px] font-semibold text-text">Motivo ante el SAT</legend>
          {(['02', '03'] as const).map((m) => (
            <label key={m} className="flex items-start gap-2 text-[12.5px] text-text-2">
              <input
                type="radio"
                name="motivo"
                value={m}
                checked={motivo === m}
                onChange={() => setMotivo(m)}
                className="mt-0.5"
              />
              {MOTIVO_LABEL[m]}
            </label>
          ))}
          <p className="mt-1 text-[11.5px] text-text-3">
            Si el error está en los datos fiscales del receptor, usa Sustituir: emite el CFDI
            corregido primero y cancela este citándolo (motivo 01), como lo exige el SAT.
          </p>
        </fieldset>
      </Modal>

      <Modal
        open={substituteFor !== null}
        onClose={() => setSubstituteFor(null)}
        title="Sustituir CFDI"
        subtitle="Se emite un CFDI nuevo con los datos fiscales ACTUALES del cliente y después se cancela este, relacionado como sustitución (motivo 01)."
        footer={
          <>
            <Button variant="secondary" onClick={() => setSubstituteFor(null)}>
              Volver
            </Button>
            <Button
              disabled={substitute.isPending}
              onClick={() => {
                if (substituteFor !== null)
                  substitute.mutate({
                    invoiceId: substituteFor.inv.invoiceId,
                    uuid: substituteFor.uuid,
                  })
              }}
            >
              {substitute.isPending ? 'Sustituyendo…' : 'Emitir sustituto y cancelar'}
            </Button>
          </>
        }
      >
        <p className="text-[12.5px] leading-relaxed text-text-2">
          Corrige primero los datos fiscales del cliente (RFC, razón social, código postal,
          régimen): el sustituto se emite con lo que esté guardado en este momento. Si algo sigue
          mal, la sustitución se rechaza antes de tocar al SAT.
        </p>
      </Modal>
    </Card>
  )
}
